import gsap from "gsap";
import observe from "./observe";

const header = {
    init() {
        this.sticky();
        this.menuLine();
        this.submenus();
        this.mobileMenu();
        this.cartBadges();
    },

    sticky() {
        const bar = document.querySelector('.header__bottom');

        if (!bar) {
            return;
        }

        observe.classChange(bar, 'fixed', (fixed) => {
            gsap.killTweensOf(bar);

            if (fixed) {
                gsap.fromTo(bar, { yPercent: -100 }, { yPercent: 0, duration: 0.6, ease: 'expo.out', clearProps: 'transform' });
            } else {
                gsap.set(bar, { clearProps: 'transform' });
            }
        });
    },

    menuLine() {
        const menu = document.querySelector('.header__menu');

        if (!menu || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
            return;
        }

        const items = Array.from(menu.children).filter((item) => item.classList.contains('header__menu-item') && !item.classList.contains('header__menu-item-cart'));

        if (!items.length) {
            return;
        }

        const line = document.createElement('span');
        let visible = false;

        line.className = 'ex-menu-line';
        line.setAttribute('aria-hidden', 'true');
        menu.appendChild(line);
        menu.classList.add('ex-line-ready');

        items.forEach((item) => {
            item.addEventListener('mouseenter', () => {
                const link = item.querySelector('.header__menu-link') || item;
                const menuRect = menu.getBoundingClientRect();
                const linkRect = link.getBoundingClientRect();
                const place = { x: linkRect.left - menuRect.left, width: linkRect.width, top: item.getBoundingClientRect().bottom - menuRect.top + 17 };

                if (visible) {
                    gsap.to(line, Object.assign(place, { opacity: 1, scaleX: 1, duration: 0.55, ease: 'expo.out', overwrite: true }));
                } else {
                    gsap.set(line, place);
                    gsap.fromTo(line, { opacity: 1, scaleX: 0 }, { scaleX: 1, duration: 0.45, ease: 'expo.out', overwrite: true });
                }

                visible = true;
            });
        });

        menu.addEventListener('mouseleave', () => {
            visible = false;
            gsap.to(line, { opacity: 0, scaleX: 0.3, duration: 0.35, ease: 'power2.in', overwrite: true });
        });
    },

    submenus() {
        document.querySelectorAll('.header__menu .header__menu-has-children').forEach((item) => {
            const submenu = item.querySelector('.header__submenu');

            if (!submenu) {
                return;
            }

            const links = submenu.querySelectorAll('.header__submenu-item');
            const chevron = item.querySelector('.header__menu-link i');

            item.addEventListener('mouseenter', () => {
                gsap.fromTo(submenu, { clipPath: 'inset(0% 0% 100% 0%)', y: -6 }, {
                    clipPath: 'inset(0% 0% 0% 0%)',
                    y: 0,
                    duration: 0.55,
                    ease: 'expo.out',
                    overwrite: true,
                    clearProps: 'clipPath,transform',
                });
                gsap.fromTo(links, { opacity: 0, y: 16 }, {
                    opacity: 1,
                    y: 0,
                    duration: 0.5,
                    stagger: 0.05,
                    delay: 0.08,
                    overwrite: true,
                    clearProps: 'opacity,transform',
                });

                if (chevron) {
                    gsap.to(chevron, { rotation: 180, duration: 0.45, ease: 'back.out(2)', overwrite: true });
                }
            });

            item.addEventListener('mouseleave', () => {
                if (chevron) {
                    gsap.to(chevron, { rotation: 0, duration: 0.35, overwrite: true });
                }
            });
        });
    },

    mobileMenu() {
        const menu = document.querySelector('.header-menu-mobile');
        const burger = document.querySelector('.header__menu-hamburger i');

        if (!menu) {
            return;
        }

        observe.classChange(menu, 'menu-open', (open) => {
            const height = menu.scrollHeight;

            gsap.killTweensOf(menu);

            if (open) {
                gsap.fromTo(menu, { maxHeight: 0, transition: 'none' }, { maxHeight: height, duration: 0.7, ease: 'expo.out', clearProps: 'maxHeight,transition' });
                gsap.fromTo(menu.children, { opacity: 0, x: -36 }, {
                    opacity: 1,
                    x: 0,
                    duration: 0.6,
                    stagger: 0.06,
                    delay: 0.1,
                    ease: 'back.out(1.6)',
                    overwrite: true,
                    clearProps: 'opacity,transform',
                });
            } else {
                gsap.fromTo(menu, { maxHeight: height, transition: 'none' }, { maxHeight: 0, duration: 0.5, ease: 'expo.inOut', clearProps: 'maxHeight,transition' });
            }

            if (burger) {
                burger.classList.toggle('fa-bars', !open);
                burger.classList.toggle('fa-times', open);
                gsap.fromTo(burger, { rotation: open ? -180 : 180, opacity: 0.3 }, { rotation: 0, opacity: 1, duration: 0.6, ease: 'back.out(2)', clearProps: 'transform,opacity' });
            }
        });
    },

    cartBadges() {
        document.querySelectorAll('.header__link__span').forEach((badge) => {
            const icon = badge.parentElement ? badge.parentElement.querySelector('.icon-cart') : null;
            let count = badge.textContent.trim();

            new MutationObserver(() => {
                const next = badge.textContent.trim();

                if (next === count) {
                    return;
                }

                count = next;
                gsap.fromTo(badge, { scale: 1.8 }, { scale: 1, duration: 0.8, ease: 'elastic.out(1, 0.4)', overwrite: true, clearProps: 'transform' });

                if (icon) {
                    gsap.fromTo(icon, { rotation: -14, transformOrigin: '50% 80%' }, {
                        rotation: 0,
                        duration: 0.9,
                        ease: 'elastic.out(1.2, 0.3)',
                        overwrite: true,
                        clearProps: 'transform,transformOrigin',
                    });
                }
            }).observe(badge, { childList: true, characterData: true, subtree: true });
        });
    },
};

export default header;
